import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { createUseStyles } from 'react-jss';
import { siteProp } from './editing/site-editing-helpers';
import routePaths from '../../route-paths';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    height: 'calc(100vh - 140px)',
  },
  mapContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  '@global': {
    '.mapboxgl-popup': {
      maxWidth: 400,
    },
  },
});

const SiteListMap = ({ sitesList, orgSiteId }) => {
  const classes = useStyles();
  const map = useRef(null);
  const mapContainer = useRef(null);

  useEffect(() => {
    const orgSite = sitesList.find((s) => s.siteType === 'ORGANIZATION');
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/navigation-day-v1',
      zoom: 4,
      center: orgSite?.physicalAddress.loc.coordinates,
      interactive: true,
      boxZoom: true,
      scrollZoom: true,
      dragPan: true,
      dragRotate: true,
      attributionControl: true,
    });

    map.current.on('load', () => {
      if (sitesList.length > 0) {
        map.current
          .getStyle()
          .layers.filter((layer) => layer.source === 'mapbox-traffic')
          .forEach((layer) => map.current.removeLayer(layer.id));
        map.current.removeLayer('settlement-major-label');
        map.current.removeLayer('settlement-minor-label');

        map.current.addSource('points', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: sitesList.map((s) => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: s.physicalAddress.loc.coordinates,
              },
              properties: {
                title: s.siteName,
                description: `<div><h4>${s.siteName}</h4><div>${s.physicalAddress.content}</div><a href="${routePaths.sites}/${orgSiteId}/${s._id}">Edit</a></div>`,
              },
            })),
          },
        });
        map.current.addLayer({
          id: 'points',
          type: 'symbol',
          source: 'points',
          layout: {
            'icon-image': 'dot-11',
            'icon-size': 2,
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 1.25],
            'text-size': 12,
            'text-anchor': 'top',
          },
        });
        const { sw, ne } = sitesList.reduce(
          (bounds, site) => {
            const coords = site.physicalAddress.loc.coordinates;
            if (!bounds.sw && !bounds.ne) {
              bounds.sw = [...coords];
              bounds.ne = [...coords];
            }
            if (bounds.sw[0] > coords[0]) bounds.sw[0] = coords[0];
            if (bounds.sw[1] > coords[1]) bounds.sw[1] = coords[1];
            if (bounds.ne[0] < coords[0]) bounds.ne[0] = coords[0];
            if (bounds.ne[1] < coords[1]) bounds.ne[1] = coords[1];
            return bounds;
          },
          { sw: null, ne: null },
        );
        const bounds = new mapboxgl.LngLatBounds(sw, ne);
        map.current.fitBounds(bounds, { maxZoom: 6, padding: 40, animate: false });

        map.current.addInteraction('points-click-interaction', {
          type: 'click',
          target: { layerId: 'points' },
          handler: (e) => {
            // Copy coordinates array.
            const coordinates = e.feature.geometry.coordinates.slice();
            const description = e.feature.properties.description;

            new mapboxgl.Popup().setLngLat(coordinates).setHTML(description).addTo(map.current);
          },
        });

        // Change the cursor to a pointer when the mouse is over a POI.
        map.current.addInteraction('points-mouseenter-interaction', {
          type: 'mouseenter',
          target: { layerId: 'points' },
          handler: () => {
            map.current.getCanvas().style.cursor = 'pointer';
          },
        });

        // Change the cursor back to a pointer when it stops hovering over a POI.
        map.current.addInteraction('points-mouseleave-interaction', {
          type: 'mouseleave',
          target: { layerId: 'points' },
          handler: () => {
            map.current.getCanvas().style.cursor = '';
          },
        });
      }
    });
    return () => {
      if (map.current) map.current.remove();
    };
  }, [sitesList]);

  return (
    <div className={classes.wrap}>
      <div ref={mapContainer} className={classes.mapContainer} />
    </div>
  );
};

SiteListMap.propTypes = {
  sitesList: PropTypes.arrayOf(siteProp),
  orgSiteId: PropTypes.string,
};

export default SiteListMap;
